import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CardFrame } from "../../../../../common/components/card-frame/CardFrame";
import { ButtonWithLoadingState } from "../../../../../common/components/button-with-loading-state/ButtonWithLoadingState";
import { PrimaryInputWithLoaderBelow } from "../../../../../common/components/primary-input-with-loader-below/PrimaryInputWithLoaderBelow";
import { HelpModal } from "../../../../../common/components/help-modal/HelpModal";
import { CircleInfoIcon } from "../../../../../assets/icons";
import { ConsumerType, CustomerEnergyAccountRequest, ResidencyStatus, VerifyNinRequest } from "../../../../../model/energy-profile";
import { useVerifyNinMutation, useUpdateEnergyAccountMutation } from "../../../../../service/vending-service";
import { RootState } from "../../../../../stores/store";
import { setEnergyProfileInfo } from "../../../../../stores/slices/customer-energy-profile-slice";
import { ApplicationRoutes } from "../../../../../routes";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import EnergyProfileSuccessModal from "./EnergyProfileSuccessModal";
import styles from "./NinVerificationPage.module.scss";

type NinErrorType = "none" | "unableToVerify" | "customerNameMismatch" | "meterNameMismatch";

interface NinPageRouteState {
	meterNumber?: string;
	isLandlord?: boolean;
	consumerType?: ConsumerType;
}

interface NinVerificationPageProps {
	/** When embedded inside the sign-up flow: render bare (no page layout), read
	 * params from `pageState` instead of the router location, and call
	 * `onComplete`/`onBack` instead of route navigation. */
	embedded?: boolean;
	pageState?: NinPageRouteState;
	onComplete?: () => void;
	onBack?: () => void;
}

/** In sign-up the FormWrapper provides the page chrome, so render bare. */
const NinVerificationLayout = ({
	embedded,
	onBack,
	children,
}: {
	embedded: boolean;
	onBack: () => void;
	children: ReactNode;
}) => {
	const { t } = useTranslation();
	return embedded ? (
		<div>{children}</div>
	) : (
		<CustomerPageLayout title={t("energyManagement.ninVerificationTitle")} showBackButton onBack={onBack} contentMaxWidth={600} decreaseMainContentPadding={true}>
			{children}
		</CustomerPageLayout>
	);
};

function classifyError(message: string, status?: number): NinErrorType {
	if (message.includes("do not match the registered name on the meter")) return "meterNameMismatch";
	if (message.includes("do not match the registered user name")) return "customerNameMismatch";
	if (status === 500 || status === 503 || message.toLowerCase().includes("unavailable")) return "unableToVerify";
	return "unableToVerify";
}

export const NinVerificationPage = ({ embedded = false, pageState, onComplete, onBack }: NinVerificationPageProps = {}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { meterNumber: routeMeterNumber = "", isLandlord = false, consumerType: routeConsumerType } = (pageState ?? location.state ?? {}) as NinPageRouteState;

	// In embedded (sign-up) mode "go back" returns to the previous phase; outside
	// sign-up it pops the route.
	const goBack = () => (embedded ? onBack?.() : navigate(-1));

	const dispatch = useDispatch();
	const storedProfile = useSelector((state: RootState) => state.customerEnergyProfile);
	const [verifyNin] = useVerifyNinMutation();
	const [updateEnergyAccount] = useUpdateEnergyAccountMutation();

	const [nin, setNin] = useState(storedProfile.nin?.trim() ?? "");
	const [isVerifying, setIsVerifying] = useState(false);
	const [hasResult, setHasResult] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [errorType, setErrorType] = useState<NinErrorType>("none");
	const [message, setMessage] = useState("");
	const [isSavingForLater, setIsSavingForLater] = useState(false);
	const [isUpdatingEnergyProfile, setIsUpdatingEnergyProfile] = useState(false);
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	const utilityId = process.env.REACT_APP_UTILITY_ID || storedProfile.utilityId || "";

	const effectiveConsumerType = routeConsumerType ?? storedProfile.consumerType;
	const consumerTypeStr = effectiveConsumerType === ConsumerType.Postpaid ? "Postpaid" : "Prepaid";

	const residencyStatusStr =
		storedProfile.residencyStatus === ResidencyStatus.Landlord
			? "Landlord"
			: storedProfile.residencyStatus === ResidencyStatus.Tenant
				? "Tenant"
				: isLandlord
					? "Landlord"
					: "Tenant";

	const meterNumberToSend = consumerTypeStr === "Prepaid" ? storedProfile.meterNumber || routeMeterNumber || undefined : undefined;

	const accountNumberToSend = consumerTypeStr === "Postpaid" ? storedProfile.accountNumber || routeMeterNumber || undefined : undefined;

	const handleNinChange = (value: string) => {
		setNin(value);
		if (hasResult) {
			setHasResult(false);
			setIsSuccess(false);
			setErrorType("none");
			setMessage("");
		}
	};

	const buildUpdateRequest = (saveAsPending: boolean): CustomerEnergyAccountRequest => ({
		utilityId,
		consumerType: consumerTypeStr,
		residencyStatus: residencyStatusStr,
		preferRemoteTokenLoad: storedProfile.preferRemoteTokenLoad ?? undefined,
		nin: nin.trim() || undefined,
		saveAsPending,
		meterNumber: meterNumberToSend,
		accountNumber: accountNumberToSend,
	});

	const updateProfileWithNin = async (saveAsPending: boolean): Promise<boolean> => {
		setIsUpdatingEnergyProfile(true);
		try {
			const updated = await updateEnergyAccount(buildUpdateRequest(saveAsPending)).unwrap();
			if (updated) dispatch(setEnergyProfileInfo(updated));
			return true;
		} catch (err: any) {
			const errMsg = err?.data?.message ?? err?.data?.title ?? t("account.web.failedToUpdateEnergyProfile");
			setErrorType("unableToVerify");
			setMessage(errMsg);
			return false;
		} finally {
			setIsUpdatingEnergyProfile(false);
		}
	};

	const submit = async () => {
		const ninValue = nin.trim();
		if (!ninValue) {
			setHasResult(true);
			setIsSuccess(false);
			setErrorType("unableToVerify");
			setMessage(t("account.web.ninIsRequired"));
			return;
		}

		setIsVerifying(true);
		setHasResult(false);
		setIsSuccess(false);
		setErrorType("none");
		setMessage("");

		const request: VerifyNinRequest = {
			nin: ninValue,
			residencyStatus: residencyStatusStr,
			consumerType: consumerTypeStr,
			utilityId,
			meterNumber: meterNumberToSend,
			accountNumber: accountNumberToSend,
		};

		try {
			await verifyNin(request).unwrap();
			setIsVerifying(false);
			setHasResult(true);
			setIsSuccess(true);
			setMessage(t("solar.ninVerification.ninVerifiedSuccess"));
			updateProfileWithNin(false);
			setShowSuccessModal(true);
		} catch (err: any) {
			setIsVerifying(false);
			const errMsg = err?.data?.message ?? err?.data?.title ?? t("account.web.verificationFailed");
			setHasResult(true);
			setIsSuccess(false);
			setErrorType(classifyError(errMsg, err?.status));
			setMessage(errMsg);
		}
	};

	const saveForLater = async () => {
		setIsSavingForLater(true);
		try {
			const updated = await updateEnergyAccount(buildUpdateRequest(true)).unwrap();
			if (updated) dispatch(setEnergyProfileInfo(updated));
			setShowSuccessModal(true);
		} catch {
			toast.error(t("account.web.failedToSaveForLater"));
		} finally {
			setIsSavingForLater(false);
		}
	};

	const handleNinVerificationSuccessRedirection = () => {
		setShowSuccessModal(true);
	};

	const renderBottomBar = () => {
		if (hasResult && isSuccess) {
			return (
				<ButtonWithLoadingState
					buttonText={t("common.actions.continueKey")}
					loading={isUpdatingEnergyProfile}
					buttonPressed={isUpdatingEnergyProfile ? null : () => handleNinVerificationSuccessRedirection()}
				/>
			);
		}

		if (hasResult && !isSuccess) {
			let primaryLabel: string;
			let primaryAction: () => void;

			if (errorType === "customerNameMismatch") {
				primaryLabel = t("solar.ninVerification.editName");
				primaryAction = () => toast.info(t("account.web.editNameNotImplemented"));
			} else if (errorType === "meterNameMismatch") {
				primaryLabel = t("solar.ninVerification.editMeterDetails");
				primaryAction = goBack;
			} else {
				primaryLabel = t("energyManagement.saveForLater");
				primaryAction = saveForLater;
			}

			return (
				<div className={styles.buttonsRow}>
					<ButtonWithLoadingState
						buttonText={primaryLabel}
						loading={isSavingForLater}
						buttonPressed={isSavingForLater ? null : primaryAction}
						isSecondary
						style={{ flex: 1 }}
					/>
					<ButtonWithLoadingState buttonText={t("energyManagement.retry")} buttonPressed={isSavingForLater ? null : submit} style={{ flex: 1 }} />
				</div>
			);
		}

		return <ButtonWithLoadingState buttonText={t("common.actions.continueKey")} buttonPressed={isVerifying ? null : submit} />;
	};

	return (
		<>
			<NinVerificationLayout embedded={embedded} onBack={goBack}>
				<div className={styles.topSpacer} />

				<CardFrame
					headerSectionTitle={t("account.web.enterYourNin")}
					headerSectionRightContent={
						<div className={styles.infoIconBtn} onClick={() => setInfoModalOpen(true)}>
							<CircleInfoIcon />
						</div>
					}
					paddingBottom={0}
				>
					<div className={styles.inputWrapper}>
						<PrimaryInputWithLoaderBelow
							value={nin}
							onChange={handleNinChange}
							placeholder={t("account.web.enterNin")}
							loading={isVerifying}
							loadingLabel={t("account.web.verifyingNin")}
							loaded={hasResult}
							loadedLabel={isSuccess ? message : null}
							loadedErrorLabel={!isSuccess && message ? message : null}
						/>
					</div>
				</CardFrame>

				<div className={styles.midSpacer} />

				<CardFrame paddingBottom={0}>
					<div className={styles.linkCardBody}>
						<p className={styles.linkText}>
							{t("account.web.needToUpdateMeterNumber")}
							<span className={styles.linkAnchor} onClick={goBack}>
								{t("solar.ninVerification.changeItAndVerifyAgain")}
							</span>
						</p>
					</div>
				</CardFrame>

				<div className={styles.bottomSpacer} />

				{renderBottomBar()}
			</NinVerificationLayout>

			<HelpModal
				isOpen={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
				title={t("account.web.ninInformation")}
				description={t("account.web.ninInformationDescription")}
			/>

			{showSuccessModal && (
				<EnergyProfileSuccessModal
					serviceType={consumerTypeStr}
					accountOrMeterNumber={meterNumberToSend ?? accountNumberToSend ?? ""}
					onEnergyProfilePage={embedded && onComplete ? onComplete : () => navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}`)}
					onBackToHome={embedded && onComplete ? onComplete : () => navigate("/dashboard")}
				/>
			)}
		</>
	);
};
